/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@lms-types/index';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  setAuth: (user: User, accessToken: string) => void;
  updateUser: (patch: Partial<User>) => void;
  clearAuth: () => void;
}

// Check if a token is a real JWT (3 base64url parts) vs old dev base64 token
function isValidJwt(token: string): boolean {
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    // Reject if already expired
    if (payload.exp && payload.exp * 1000 < Date.now()) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Does a rehydrated session disagree with what the server now reports?
 *
 * Two ways it can, and both mean the cached session has to go rather than be
 * patched up:
 *
 *  - **A different user.** The stored blob belongs to somebody else.
 *  - **A different role.** The stored role and the access token are written together
 *    by `setAuth` and never diverge (`updateUser` only ever patches `fullName`), so
 *    the stored role is also the role the token carries — and the token is what the
 *    backend authorizes on. A server role that differs means this session predates a
 *    privilege change: an Entra group edit picked up by a later sign-in, or an admin
 *    edit.
 *
 * Why clear rather than adopt the server's role: the token is unchanged, so writing
 * the new role into the store alone would render an admin surface that every request
 * 403s, or hide one the token still permits. Signing in again mints a token and a
 * role that agree.
 *
 * Missing fields on either side are *not* a mismatch — a server that did not send a
 * role is not a server reporting a different one, and must not log anyone out.
 *
 * Pure and exported so the rule is unit-tested rather than only reachable through
 * zustand's rehydration callback.
 */
export function isStaleSession(
  stored: { id?: string; role?: string } | null | undefined,
  server: { id?: string; role?: string } | null | undefined
): boolean {
  if (!server) return false;
  if (server.id !== stored?.id) return true;
  return Boolean(server.role && stored?.role && server.role !== stored.role);
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,

      setAuth: (user, accessToken) => set({ user, accessToken, isAuthenticated: true }),

      updateUser: (patch) =>
        set((state) => (state.user ? { user: { ...state.user, ...patch } } : state)),

      clearAuth: () => set({ user: null, accessToken: null, isAuthenticated: false }),
    }),
    {
      name: 'lms-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }),
      // On rehydration — reject old dev tokens, expired JWTs, and sessions the server
      // no longer agrees with, by user or by role.
      onRehydrateStorage: () => (state) => {
        if (!state?.accessToken || !isValidJwt(state.accessToken)) {
          if (state) {
            state.user = null;
            state.accessToken = null;
            state.isAuthenticated = false;
          }
          return;
        }
        // Verify token against server — clears session only on explicit 401,
        // NOT on network errors / timeouts (which happen during Azure cold starts).
        // Raw `fetch` on purpose: `api` imports this store (a cycle), and its 401
        // interceptor would also redirect to /login, which a rehydration check
        // must not do. The token is the one in hand, not one to look up.
        fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${state.accessToken}` },
        })
          .then((res) => {
            if (res.status === 401 || res.status === 403) {
              useAuthStore.getState().clearAuth();
              return;
            }
            if (!res.ok) return; // server error / 5xx — keep session, don't wipe user
            return res.json();
          })
          .then((body?: { user: User }) => {
            if (!body) return;
            // A different user, or a role the server has moved on from — see
            // isStaleSession. Either way the cached session is cleared, not repaired.
            if (isStaleSession(state.user, body.user)) {
              useAuthStore.getState().clearAuth();
            }
          })
          .catch(() => {
            // Network error / timeout — keep the cached session intact.
            // The 401 interceptor on `api` will clear auth if any real
            // API call later returns unauthorized.
          });
      },
    }
  )
);
